import React, { Fragment, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useCategories } from "../../../hooks/apiHooks/useCategories";
import { saveCollabInterests } from "../../../config/apisauce";
import useNotiStack from "../../../hooks/useNotiStack";
import { CheckboxGroup, CategorySection } from "../../../components/Inputs/InterestMultiSelectCheckbox";
import { EditProfileButtons } from "./EditProfileButtons";
import { useFormik } from "formik";
import * as Yup from "yup";
import { COLLAB_INTEREST_NSFW, COLLAB_INTEREST_SFW } from "../../../enums/generalEnums";

const validationSchema = Yup.object({
  collab_interest_sfw: Yup.array().test(
    "collab-sfw-required",
    "Please select at least one SFW interest or mark 'Not Interested'.",
    (value) => value.length > 0
  ),
  collab_interest_nsfw: Yup.array().test(
    "collab-nsfw-required",
    "Please select at least one NSFW interest or mark 'Not Interested'.",
    (value) => value.length > 0
  ),
});

export const EditCollabInterestForm = ({ creatorDetails, handleClose, refetchDetails }) => {
  const { success } = useNotiStack();
  const [loading, setLoading] = useState(false);
  const [showCollabSFW, setShowCollabSFW] = useState(true);

  // prettier-ignore
  const { data: categories = [], isLoading, error } = useCategories({
    type: [COLLAB_INTEREST_SFW, COLLAB_INTEREST_NSFW],
  });

  // Group categories by type
  const groupedCategories = categories.reduce((acc, category) => {
    acc[category.type] = acc[category.type] || [];
    acc[category.type].push(category);
    return acc;
  }, {});

  const formik = useFormik({
    initialValues: {
      collab_interest_sfw: creatorDetails?.user?.collab_interest?.filter((item) => item?.type === COLLAB_INTEREST_SFW) || [],
      collab_interest_nsfw: creatorDetails?.user?.collab_interest?.filter((item) => item?.type === COLLAB_INTEREST_NSFW) || [],
    },
    validationSchema,
    onSubmit: async (values, { setErrors }) => {
      setLoading(true);
      try {
        const isSfwNotInterested = values.collab_interest_sfw.length === 1 && values.collab_interest_sfw[0] === "";
        const isNsfwNotInterested = values.collab_interest_nsfw.length === 1 && values.collab_interest_nsfw[0] === "";

        let categories_id = [];

        if (isSfwNotInterested && isNsfwNotInterested) {
          categories_id = [null, null];
        } else {
          categories_id = [...values.collab_interest_sfw, ...values.collab_interest_nsfw]
            .filter((item) => item !== "")
            .map((item) => item.id);
        }

        const params = {
          user_id: creatorDetails?.user_id,
          categories_id,
        };

        const response = await saveCollabInterests(params);
        if (response.ok) {
          success("Successfully updated collab interests!");
          handleClose();
          refetchDetails();
        } else {
          setErrors({ submit: "Failed to save interests. Please try again." });
        }
      } catch (error) {
        setErrors({ submit: error.message });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCheckboxChange = (interest, categoryType) => {
    let selectedCategories = formik.values[categoryType] || [];
    selectedCategories = selectedCategories.filter((item) => item !== "");
    const isSelected = selectedCategories.some((item) => item?.id === interest.id);
    const updated = isSelected
      ? selectedCategories.filter((item) => item.id !== interest.id)
      : [...selectedCategories, interest];
    formik.setFieldValue(categoryType, updated);
  };

  const handleNotInterestedChange = (checked, categoryType) => {
    if (checked) {
      formik.setFieldValue(categoryType, [""]);
    } else {
      const selectedCategories = formik.values[categoryType] || [];
      const updated = selectedCategories.filter((item) => item !== "");
      formik.setFieldValue(categoryType, updated);
    }
  };

  // Calculate selected IDs for button disabling
  const collabInterestSelectedIds = [...formik.values.collab_interest_sfw, ...formik.values.collab_interest_nsfw]
    .filter((item) => item !== "")
    .map((item) => item.id);

  if (isLoading) {
    return <Box>Loading categories...</Box>;
  }

  if (error) {
    return <Box color="error">Failed to load categories. Please try again.</Box>;
  }

  return (
    <Fragment>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        <CategorySection
          title="Collaboration Interest"
          error={formik.errors.collab_interest_sfw || formik.errors.collab_interest_nsfw}
          showSFW={showCollabSFW}
          setShowSFW={setShowCollabSFW}
          categoryType={showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW}
        />

        <CheckboxGroup
          categories={groupedCategories[showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW] || []}
          selectedCategories={formik.values[showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW] || []}
          handleCheckboxChange={handleCheckboxChange}
          handleNotInterestedChange={handleNotInterestedChange}
          categoryType={showCollabSFW ? COLLAB_INTEREST_SFW : COLLAB_INTEREST_NSFW}
        />

        {formik.errors.submit && <Typography color="error">{formik.errors.submit}</Typography>}

        <EditProfileButtons
          handCancel={handleClose}
          handleSubmit={formik.handleSubmit}
          disabled={
            collabInterestSelectedIds.length < 1 &&
            !formik.values.collab_interest_sfw.includes("") &&
            !formik.values.collab_interest_nsfw.includes("")
          }
          loading={loading}
        />
      </Box>
    </Fragment>
  );
};
